import React, { useMemo } from 'react'
import {
  DataTable,
  LinkButton,
  type DataTableColumn,
} from '@enamelai/design-system'
import InsuranceEmptyCard from '@components/pages/Insurance/InsuranceEmptyCard/InsuranceEmptyCard.component'
import { InsurancePlan } from '@dataTypes/insurance.types'
import {
  checkPatientEligibility,
  setVerifiedOnInsurancePlanById,
} from '@store/actions/insurance.action'
import { useAppDispatch } from '@store/hooks'
import { AppDispatch } from '@store/index'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { GlobalLoadingOptions } from '@context/GlobalLoadingContext'
import { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'

import { logger } from '@utils/safeLogger'

interface PlanDetailsProps {
  insurancePlan?: InsurancePlan
  outlined?: boolean
}

const handleVerifyInsurancePlanClick = async (
  insurancePlan: any,
  dispatch: AppDispatch,
  showLoading: (options: GlobalLoadingOptions) => void,
  hideLoading: (container: string) => void,
  showToast: (toast: NotificationToastData) => void,
): Promise<void> => {
  showLoading({
    container: '.plan-details-container',
    hideElements: ['.insurance-container > .insurance-tab-content'],
  })
  const formData = {
    patientId: insurancePlan.patient_member_id,
    patientDob: insurancePlan.patient_dob,
  }
  // Remove this once we have production ready DentalXChange API
  // Mock service is used to simulate the third-party system while keeping our application aligned with their data structure.
  const dataForMockService = {
    ...formData,
    patientDetails: {
      memberId: insurancePlan.patient_member_id,
      firstName: insurancePlan.patient_first_name,
      lastName: insurancePlan.patient_last_name,
      dateOfBirth: insurancePlan.patient_dob.split('T')[0],
      address: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
      },
    },
    existingPatientPayers: [],
  }
  await dispatch(checkPatientEligibility(dataForMockService))
    .then(async (state) => {
      const planActive =
        state?.payload?.response?.eligibilities?.[0]?.insurance?.planStatus ===
        'Active'
          ? true
          : false
      if (planActive) {
        await dispatch(
          setVerifiedOnInsurancePlanById(insurancePlan.insurance_plan_id),
        )
        setTimeout(() => {
          hideLoading('.plan-details-container')
          showToast(
            createSuccessToast(
              'Insurance plan verified successfully',
              'Insurance plan has been verified successfully.',
            ),
          )
        }, 2000)
      } else {
        setTimeout(() => {
          hideLoading('.plan-details-container')
          showToast(
            createFailedToast(
              'Insurance plan not verified',
              'Insurance plan not verified. Please try again.',
            ),
          )
        }, 2000)
      }
    })
    .catch((error) => {
      logger.error('Error occurred:', error)
      setTimeout(() => {
        hideLoading('.plan-details-container')
        showToast(
          createFailedToast(
            'Insurance plan not verified',
            'Insurance plan not verified. Please try again.',
          ),
        )
      }, 2000)
    })
}

type PlanDetailsRow = { id: string }

const PlanDetails: React.FC<PlanDetailsProps> = ({
  insurancePlan,
  outlined = false,
}) => {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()

  const rows = useMemo<PlanDetailsRow[]>(
    () =>
      insurancePlan ? [{ id: insurancePlan.insurance_plan_id ?? 'plan' }] : [],
    [insurancePlan],
  )

  const columns = useMemo<DataTableColumn<PlanDetailsRow>[]>(() => {
    const plan = insurancePlan as any
    const orDash = (value: unknown): React.ReactNode =>
      (value as string | undefined) || '-'
    return [
      {
        id: 'patient_fullname',
        label: 'Patient name',
        renderCell: () => orDash(plan?.patient_fullname),
      },
      {
        id: 'patient_dob',
        label: 'Date of birth',
        renderCell: () => orDash(plan?.patient_dob),
      },
      {
        id: 'group_number',
        label: 'Group number',
        renderCell: () => orDash(plan?.group_number),
      },
      {
        id: 'start_date',
        label: 'Start date',
        renderCell: () => orDash(plan?.start_date),
      },
      {
        id: 'end_date',
        label: 'End date',
        renderCell: () => orDash(plan?.end_date),
      },
      {
        id: 'effective_date',
        label: 'Effective date',
        renderCell: () => orDash(plan?.effective_date),
      },
      {
        id: 'verified_on',
        label: 'Verified on',
        renderCell: () => (
          <span className="insurance-verified-on">
            {plan?.verified_on}
            {plan?.verified_on !== '-' && (
              <LinkButton
                size="sm"
                onClick={() =>
                  handleVerifyInsurancePlanClick(
                    insurancePlan,
                    dispatch,
                    showLoading,
                    hideLoading,
                    showToast,
                  )
                }
              >
                Verify
              </LinkButton>
            )}
          </span>
        ),
      },
    ]
  }, [insurancePlan, dispatch, showLoading, hideLoading, showToast])

  return (
    <div className="section-insurance-details">
      {insurancePlan ? (
        <div className="insurance-table">
          <DataTable<PlanDetailsRow>
            columns={columns}
            rows={rows}
            showActions={false}
          />
        </div>
      ) : (
        <InsuranceEmptyCard
          iconClass="mgc mgc_shield_fill"
          emptyTitle="No plan details found"
          description="You don't have any plan details on record yet."
          outlined={outlined}
        />
      )}
    </div>
  )
}

export default PlanDetails
